import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';

interface AttendanceRecord {
  id: string;
  employee_id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  transaction_date: string;
  clock_in_time: string;
  clock_out_time: string;
  normal_working_hours: number;
  lateness_minutes: number;
  ot_1_5_hours: number;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const todayMonth = () => new Date().toISOString().slice(0, 7);
const todayDate = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ fromDate: '', toDate: '' });
  const [formData, setFormData] = useState({
    employeeId: '',
    transactionDate: '',
    clockInTime: '',
    clockOutTime: '',
    normalWorkingHours: 8,
    reasonCode: '',
    comments: '',
  });

  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processMode, setProcessMode] = useState<'day' | 'month'>('day');
  const [processForm, setProcessForm] = useState({ employeeId: '', date: todayDate(), month: todayMonth() });
  const [processResult, setProcessResult] = useState<any>(null);
  const [processing, setProcessing] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importResult, setImportResult] = useState<any>(null);

  const [otSummary, setOtSummary] = useState<any>(null);

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttendance = async (customFilters?: { fromDate?: string; toDate?: string }) => {
    try {
      setLoading(true);
      const params: any = {};
      const f = customFilters || filters;
      if (f.fromDate) params.fromDate = f.fromDate;
      if (f.toDate) params.toDate = f.toDate;
      const response = await api.get('/tms/attendance', { params });
      setAttendance(response.data.attendance || []);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/tms/attendance/manual', formData);
      setFormData({
        employeeId: '',
        transactionDate: '',
        clockInTime: '',
        clockOutTime: '',
        normalWorkingHours: 8,
        reasonCode: '',
        comments: '',
      });
      setShowForm(false);
      fetchAttendance();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to record attendance');
    }
  };

  const handleProcess = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    setProcessResult(null);
    try {
      if (processMode === 'day') {
        const res = await api.post('/tms/attendance/process', {
          employeeId: processForm.employeeId,
          date: processForm.date,
        });
        setProcessResult(res.data.result);
      } else {
        const res = await api.post('/tms/attendance/process-month', {
          employeeId: processForm.employeeId || undefined,
          month: processForm.month,
        });
        setProcessResult(res.data.summary);
      }
      fetchAttendance();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to process attendance');
    } finally {
      setProcessing(false);
    }
  };

  const checkOtCap = async () => {
    if (!processForm.employeeId) {
      alert('Select an employee first');
      return;
    }
    try {
      const res = await api.get('/tms/attendance/monthly-ot-summary', {
        params: { employeeId: processForm.employeeId, month: processForm.month },
      });
      setOtSummary(res.data.summary);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to fetch OT summary');
    }
  };

  const handleImport = async () => {
    const lines = csvText.trim().split('\n').filter(Boolean);
    if (lines.length < 2) {
      alert('Paste CSV with a header row: employeeCode,date,clockInTime,clockOutTime');
      return;
    }
    const rows = lines.slice(1).map((line) => {
      const [employeeCode, date, clockInTime, clockOutTime] = line.split(',').map((s) => s.trim());
      return { employeeCode, date, clockInTime, clockOutTime };
    });
    try {
      const res = await api.post('/tms/attendance/import', { rows });
      setImportResult(res.data.results);
      fetchAttendance();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Import failed');
    }
  };

  const columns: Column<AttendanceRecord>[] = [
    { key: 'employee_code', header: 'Code' },
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: 'transaction_date',
      header: 'Date',
      sortable: true,
      render: (row) => new Date(row.transaction_date).toLocaleDateString(),
    },
    { key: 'clock_in_time', header: 'Clock In' },
    { key: 'clock_out_time', header: 'Clock Out' },
    {
      key: 'normal_working_hours',
      header: 'Normal Hrs',
      render: (row) => `${row.normal_working_hours ?? 0}h`,
    },
    {
      key: 'lateness_minutes',
      header: 'Lateness',
      render: (row) => (
        <span className={row.lateness_minutes > 0 ? 'text-red-600' : ''}>{row.lateness_minutes ?? 0}m</span>
      ),
    },
    {
      key: 'ot_1_5_hours',
      header: 'OT (1.5x)',
      render: (row) => <span className="text-blue-600">{row.ot_1_5_hours ?? 0}h</span>,
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Attendance Management</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            Import CSV
          </Button>
          <Button variant="secondary" onClick={() => setShowProcessModal(true)}>
            Process Attendance
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add Manual Entry'}</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex gap-4 items-end">
        <Input
          label="From Date"
          type="date"
          value={filters.fromDate}
          onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
        />
        <Input
          label="To Date"
          type="date"
          value={filters.toDate}
          onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
        />
        <Button onClick={() => fetchAttendance()}>Filter</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <LookupSelect
                label="Employee"
                value={formData.employeeId}
                onChange={(value) => setFormData({ ...formData, employeeId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
                required
              />
              <Input
                label="Date"
                type="date"
                value={formData.transactionDate}
                onChange={(e) => setFormData({ ...formData, transactionDate: e.target.value })}
                required
              />
              <Input
                label="Clock In Time"
                type="time"
                value={formData.clockInTime}
                onChange={(e) => setFormData({ ...formData, clockInTime: e.target.value })}
              />
              <Input
                label="Clock Out Time"
                type="time"
                value={formData.clockOutTime}
                onChange={(e) => setFormData({ ...formData, clockOutTime: e.target.value })}
              />
              <Input
                label="Normal Working Hours"
                type="number"
                value={formData.normalWorkingHours}
                onChange={(e) => setFormData({ ...formData, normalWorkingHours: parseInt(e.target.value) })}
              />
              <Input
                label="Reason Code"
                placeholder="LEAVE, REST, etc."
                value={formData.reasonCode}
                onChange={(e) => setFormData({ ...formData, reasonCode: e.target.value })}
              />
            </div>
            <Button type="submit" className="w-full">
              Record Attendance
            </Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={attendance} loading={loading} emptyMessage="No attendance records" />

      <Modal isOpen={showProcessModal} onClose={() => setShowProcessModal(false)} title="Process Attendance">
        <form onSubmit={handleProcess} className="space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={processMode === 'day'} onChange={() => setProcessMode('day')} />
              Single Day
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={processMode === 'month'} onChange={() => setProcessMode('month')} />
              Full Month
            </label>
          </div>
          <LookupSelect
            label={processMode === 'month' ? 'Employee (leave blank for all)' : 'Employee'}
            value={processForm.employeeId}
            onChange={(value) => setProcessForm({ ...processForm, employeeId: value })}
            fetchOptions={fetchEmployeeOptions}
            placeholder="Search employee..."
            required={processMode === 'day'}
          />
          {processMode === 'day' ? (
            <Input
              label="Date"
              type="date"
              value={processForm.date}
              onChange={(e) => setProcessForm({ ...processForm, date: e.target.value })}
              required
            />
          ) : (
            <Input
              label="Month"
              type="month"
              value={processForm.month}
              onChange={(e) => setProcessForm({ ...processForm, month: e.target.value })}
              required
            />
          )}
          <div className="flex gap-2">
            <Button type="submit" loading={processing} className="flex-1">
              Process
            </Button>
            <Button type="button" variant="secondary" onClick={checkOtCap}>
              Check 72hr OT Cap
            </Button>
          </div>

          {otSummary && (
            <div
              className={`rounded-lg p-3 text-sm ${
                otSummary.exceedsStatutoryCap ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}
            >
              Total OT this month: {otSummary.totalOtHours}h / {otSummary.capHours}h statutory cap
              {otSummary.exceedsStatutoryCap && ' — EXCEEDS CAP, review before payroll.'}
            </div>
          )}

          {processResult && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs overflow-x-auto">
              <pre>{JSON.stringify(processResult, null, 2)}</pre>
            </div>
          )}
        </form>
      </Modal>

      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)} title="Import Attendance CSV">
        <p className="text-sm text-gray-600 mb-3">
          Paste CSV exported from your clock-in reader/device. Header row required:{' '}
          <code className="bg-gray-100 px-1">employeeCode,date,clockInTime,clockOutTime</code>
        </p>
        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={8}
          className="w-full border rounded-lg p-3 font-mono text-xs mb-3"
          placeholder={'employeeCode,date,clockInTime,clockOutTime\nEMP001,2026-06-01,09:05:00,18:10:00'}
        />
        <Button onClick={handleImport} className="w-full mb-3">
          Import
        </Button>
        {importResult && (
          <div className="bg-gray-50 rounded-lg p-3 text-xs max-h-48 overflow-y-auto">
            <pre>{JSON.stringify(importResult, null, 2)}</pre>
          </div>
        )}
      </Modal>
    </div>
  );
}
